import type { PasskeyMetadata } from '../../services/passkey-metadata'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  associateWebAuthnCredential,
  deleteWebAuthnCredential,
  listWebAuthnCredentials
} from 'aws-amplify/auth'
import { Amplify } from 'aws-amplify'
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Tag,
  Typography
} from 'antd'
import {
  DeleteOutlined,
  KeyOutlined,
  LockOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyOutlined,
  SyncOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'

import { createAwsConfig } from '../../aws/exports'
import PasskeyMetadataService from '../../services/passkey-metadata'
import { getDeviceInfo, suggestDeviceName } from '../../utils/device-info'

interface WebAuthnCredential {
  credentialId: string
  friendlyName?: string
  createdAt: number
  relyingPartyId: string
}

interface EnrichedCredential extends WebAuthnCredential {
  metadata?: PasskeyMetadata
}

const PASSKEY_QUERY_KEY = ['passkeys', 'credentials'] as const

const formatRelative = (value?: string | number | null) => {
  if (!value) {
    return 'Never'
  }

  const date = typeof value === 'number' ? new Date(value) : new Date(value)
  return `${dayjs(date).fromNow()} (${dayjs(date).format('MMM D, YYYY h:mm A')})`
}

const deviceTypeColor: Record<string, string> = {
  mobile: 'green',
  tablet: 'purple',
  desktop: 'blue'
}

const PasskeyListItem = ({
  credential,
  onRename,
  onDelete,
  deleting
}: {
  credential: EnrichedCredential
  onRename: (id: string, name: string) => Promise<void>
  onDelete: (credential: EnrichedCredential) => void
  deleting: boolean
}) => {
  const [name, setName] = useState(credential.friendlyName || '')
  const [isSaving, setIsSaving] = useState(false)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedRef = useRef(credential.friendlyName || '')

  useEffect(() => {
    setName(credential.friendlyName || '')
    lastSavedRef.current = credential.friendlyName || ''
  }, [credential.credentialId, credential.friendlyName])

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    const trimmed = name.trim()
    const previous = (lastSavedRef.current || '').trim()

    if (!trimmed || trimmed === previous) {
      setIsSaving(false)
      return
    }

    setIsSaving(true)
    debounceRef.current = setTimeout(() => {
      onRename(credential.credentialId, trimmed)
        .then(() => {
          lastSavedRef.current = trimmed
        })
        .catch(() => {
          message.error('Failed to rename device')
          setName(lastSavedRef.current)
        })
        .finally(() => setIsSaving(false))
    }, 700)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [name, credential.credentialId, onRename])

  const metadata = credential.metadata
  const deviceTagColor = metadata?.device_type
    ? deviceTypeColor[metadata.device_type] || 'default'
    : 'default'

  return (
    <Card size='small' className='shadow-sm' style={{ marginBottom: 16 }}>
      <Space direction='vertical' style={{ width: '100%' }} size='small'>
        <Space align='center' style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <KeyOutlined />
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ minWidth: 260 }}
              placeholder='Device name'
              suffix={isSaving ? <Spin size='small' /> : undefined}
            />
          </Space>
          <Popconfirm
            title='Remove passkey'
            description='Are you sure you want to remove this passkey?'
            onConfirm={() => onDelete(credential)}
          >
            <Button icon={<DeleteOutlined />} danger loading={deleting} disabled={deleting}>
              Remove
            </Button>
          </Popconfirm>
        </Space>

        <Space wrap>
          {metadata?.device_type && <Tag color={deviceTagColor}>{metadata.device_type}</Tag>}
          {metadata?.browser && <Tag>{metadata.browser}</Tag>}
          {metadata?.operating_system && <Tag>{metadata.operating_system}</Tag>}
          {metadata?.sync_provider && (
            <Tag icon={<SyncOutlined />} color={metadata.is_synced ? 'processing' : undefined}>
              {metadata.sync_provider}
            </Tag>
          )}
          {metadata?.authenticator_attachment && <Tag>{metadata.authenticator_attachment}</Tag>}
          {!metadata && <Tag color='default'>Metadata unavailable</Tag>}
        </Space>

        <Row gutter={16}>
          <Col span={12}>
            <Typography.Text type='secondary'>Relying party</Typography.Text>
            <div>{credential.relyingPartyId}</div>
          </Col>
          <Col span={12}>
            <Typography.Text type='secondary'>Last used</Typography.Text>
            <div>{formatRelative(metadata?.last_used)}</div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Typography.Text type='secondary'>Registered</Typography.Text>
            <div>{formatRelative(credential.createdAt)}</div>
          </Col>
          <Col span={12}>
            <Typography.Text type='secondary'>Screen & locale</Typography.Text>
            <div>
              {metadata?.screen_resolution || 'Unknown'} · {metadata?.language || 'Unknown'}
            </div>
          </Col>
        </Row>
      </Space>
    </Card>
  )
}

export function PasskeySettingsPage() {
  const isWebAuthnSupported = typeof window !== 'undefined' && !!window.PublicKeyCredential
  const queryClient = useQueryClient()
  const [isModalVisible, setModalVisible] = useState(false)
  const [deviceName, setDeviceName] = useState('')

  useEffect(() => {
    Amplify.configure(createAwsConfig())
  }, [])

  const fetchCredentials = async (): Promise<EnrichedCredential[]> => {
    const [cognitoResult, metadataList] = await Promise.all([
      listWebAuthnCredentials(),
      PasskeyMetadataService.getAll()
    ])

    const metadataMap = new Map(metadataList.map(item => [item.credential_id, item]))
    const credentials = ((cognitoResult.credentials || []) as unknown as WebAuthnCredential[]).map(
      cred => {
        const metadata = metadataMap.get(cred.credentialId)
        return {
          credentialId: cred.credentialId,
          friendlyName: metadata?.device_name || cred.friendlyName || 'Security key',
          createdAt: cred.createdAt,
          relyingPartyId: cred.relyingPartyId,
          metadata
        }
      }
    )
    credentials.sort((a, b) => {
      const aTime = a.metadata?.last_used ? new Date(a.metadata.last_used).getTime() : a.createdAt
      const bTime = b.metadata?.last_used ? new Date(b.metadata.last_used).getTime() : b.createdAt
      return bTime - aTime
    })

    const activeIds = credentials.map(c => c.credentialId)
    if (metadataList.length > 0) {
      try {
        await PasskeyMetadataService.cleanupOrphaned(activeIds)
      } catch (cleanupError) {
        console.warn('Failed to cleanup passkey metadata', cleanupError)
      }
    }

    return credentials
  }

  const {
    data: credentials,
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: PASSKEY_QUERY_KEY,
    queryFn: fetchCredentials,
    enabled: isWebAuthnSupported
  })

  const registerMutation = useMutation({
    mutationFn: async (name: string) => {
      let credentialId: string | undefined
      try {
        await associateWebAuthnCredential()
        const result = await listWebAuthnCredentials()
        const credentialsList = (result.credentials ?? []).slice()
        const newest = credentialsList.sort((a, b) => {
          const right = b?.createdAt?.getMilliseconds() ?? 0
          const left = a?.createdAt?.getMilliseconds() ?? 0
          return right - left
        })[0]
        credentialId = newest?.credentialId
      } catch (error) {
        if (error instanceof Error) {
          if (error?.name === 'NotAllowedError') {
            throw new Error('Registration cancelled or timed out.')
          }
          if (error?.message?.includes('Relying party does not match')) {
            throw new Error('Domain is not configured for passkeys in Cognito.')
          }
          throw error
        }
      }

      if (!credentialId) {
        throw new Error('Missing credential identifier from AWS Cognito response.')
      }

      const deviceInfo = getDeviceInfo('platform')
      const fullBrowser = [deviceInfo.browser, deviceInfo.browserVersion].filter(Boolean).join(' ')
      const fullOS = [deviceInfo.os, deviceInfo.osVersion].filter(Boolean).join(' ')

      await PasskeyMetadataService.upsert({
        credentialId,
        deviceName: name || suggestDeviceName(deviceInfo),
        browser: fullBrowser,
        operatingSystem: fullOS,
        deviceType: deviceInfo.deviceType,
        isSynced: deviceInfo.isSynced,
        syncProvider: deviceInfo.syncProvider,
        authenticatorAttachment: deviceInfo.authenticatorAttachment,
        platform: deviceInfo.platform,
        screenResolution: deviceInfo.screenResolution,
        timezone: deviceInfo.timezone,
        language: deviceInfo.language
      })
    },
    onSuccess: () => {
      message.success('Passkey registered successfully')
      setModalVisible(false)
      setDeviceName('')
      queryClient.invalidateQueries({ queryKey: PASSKEY_QUERY_KEY })
    },
    onError: (error: Error) => {
      message.error(error?.message || 'Unable to register passkey')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (credential: EnrichedCredential) => {
      await deleteWebAuthnCredential({ credentialId: credential.credentialId })
      await PasskeyMetadataService.delete(credential.credentialId).catch(() => undefined)
    },
    onSuccess: () => {
      message.success('Passkey removed')
      queryClient.invalidateQueries({ queryKey: PASSKEY_QUERY_KEY })
    },
    onError: error => {
      message.error((error as Error)?.message || 'Failed to remove passkey')
    }
  })

  const updateNameMutation = useMutation({
    mutationFn: ({ credentialId, name }: { credentialId: string; name: string }) =>
      PasskeyMetadataService.updateDeviceName(credentialId, name),
    onError: () => {
      message.error('Unable to rename device. Please try again.')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PASSKEY_QUERY_KEY })
    }
  })

  const handleRename = async (credentialId: string, name: string) => {
    await updateNameMutation.mutateAsync({ credentialId, name })
    return
  }

  const handleDelete = (credential: EnrichedCredential) => {
    deleteMutation.mutate(credential)
  }

  if (!isWebAuthnSupported) {
    return (
      <Card title='Account security'>
        <Alert
          type='warning'
          message='Your browser does not support passkeys yet.'
          description='Please use a modern browser like Chrome, Edge, Firefox, or Safari to manage security keys.'
        />
      </Card>
    )
  }

  return (
    <Space direction='vertical' size='large' style={{ width: '100%' }}>
      <Card
        title={
          <Space>
            <SafetyOutlined /> Account security & passkeys
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} disabled={isLoading}>
              Refresh
            </Button>
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
              disabled={registerMutation.isPending}
            >
              Add passkey
            </Button>
          </Space>
        }
      >
        <Alert
          type='info'
          showIcon
          message='Sign in without typing passwords'
          description='Passkeys let you use Touch ID, Face ID, or Windows Hello to sign in. Register at least two devices so you have a backup.'
          style={{ marginBottom: 24 }}
        />

        {isLoading && (
          <div className='flex justify-center py-8'>
            <Spin />
          </div>
        )}

        {isError && (
          <Alert type='error' message='Failed to load passkeys. Try refreshing the page.' />
        )}

        {!isLoading && !isError && (credentials?.length ?? 0) === 0 && (
          <Empty description='No passkeys yet'>
            <Button type='primary' onClick={() => setModalVisible(true)} icon={<LockOutlined />}>
              Add your first passkey
            </Button>
          </Empty>
        )}

        {!isLoading && !isError && (credentials?.length ?? 0) > 0 && (
          <div>
            {credentials?.map(credential => (
              <PasskeyListItem
                key={credential.credentialId}
                credential={credential}
                onRename={handleRename}
                onDelete={handleDelete}
                deleting={deleteMutation.isPending}
              />
            ))}
            <Divider />
            <Alert
              type='success'
              showIcon
              message='All set!'
              description='Passkeys are stored in AWS Cognito. You can remove them at any time from this page.'
            />
          </div>
        )}
      </Card>

      <Modal
        title='Register a new passkey'
        open={isModalVisible}
        okText={registerMutation.isPending ? 'Registering…' : 'Register passkey'}
        confirmLoading={registerMutation.isPending}
        onOk={() => registerMutation.mutate(deviceName.trim())}
        onCancel={() => {
          if (!registerMutation.isPending) {
            setModalVisible(false)
            setDeviceName('')
          }
        }}
      >
        <Typography.Paragraph>
          When prompted, approve the request using Touch ID, Face ID, Windows Hello, or your
          security key.
        </Typography.Paragraph>
        <Typography.Paragraph type='secondary'>
          If a password manager pops up, cancel it and continue with your device.
        </Typography.Paragraph>
        <Input
          placeholder='Device name (e.g., MacBook Pro, YubiKey, iPhone)'
          value={deviceName}
          onChange={e => setDeviceName(e.target.value)}
        />
        <Typography.Paragraph type='secondary' style={{ marginTop: 12 }}>
          Tip: add at least two passkeys so you are never locked out.
        </Typography.Paragraph>
      </Modal>
    </Space>
  )
}
